import React, { useState } from 'react';
import { Switch, Route, NavLink, useHistory, useLocation } from 'react-router-dom';
import { FiGrid, FiCalendar, FiUsers, FiTag, FiBriefcase, FiFlag, FiSettings, FiLogOut, FiMenu, FiX, FiShield, FiAward, FiMapPin, FiPercent, FiTarget, FiPlay, FiVideo } from 'react-icons/fi';
import Dashboard from '../pages/Dashboard';
import Contests from '../pages/Contests';
import Users from '../pages/Users';
import Categories from '../pages/Categories';
import Cities from '../pages/Cities';
import Partners from '../pages/Partners';
import Coupons from '../pages/Coupons';
import Reports from '../pages/Reports';
import Settings from '../pages/Settings';
import PartnerVerifications from '../pages/PartnerVerifications';
import Drops from '../pages/Drops';
import CreateDrop from '../pages/CreateDrop';
import EditDrop from '../pages/EditDrop';
import Games from '../pages/Games';
import PlayReels from '../pages/PlayReels';

const navItems = [
  { path: '/app/dashboard', icon: FiGrid, label: 'Dashboard' },
  { path: '/app/playables', icon: FiVideo, label: 'Play Reels Feed' },
  { path: '/app/games', icon: FiPlay, label: 'Games Manager' },
  { path: '/app/verifications', icon: FiShield, label: 'Partner Verifications' },
  { path: '/app/drops', icon: FiTarget, label: 'GPS Cash Drops' },
  { path: '/app/coupons', icon: FiPercent, label: 'Coupons' },
  { path: '/app/contests', icon: FiAward, label: 'Contests' },
  { path: '/app/users', icon: FiUsers, label: 'Users' },
  { path: '/app/categories', icon: FiTag, label: 'Categories' },
  { path: '/app/cities', icon: FiMapPin, label: 'Cities' },
  { path: '/app/partners', icon: FiBriefcase, label: 'Partners' },
  { path: '/app/reports', icon: FiFlag, label: 'Reports' },
  { path: '/app/settings', icon: FiSettings, label: 'Settings' },
];

const formatName = (nameObj) => {
  if (!nameObj) return 'Admin';
  if (typeof nameObj === 'string') return nameObj;
  if (typeof nameObj === 'object') {
    return `${nameObj.first || ''} ${nameObj.last || ''}`.trim() || 'Admin';
  }
  return 'Admin';
};

const AdminLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const history = useHistory();
  const location = useLocation();
  const user = JSON.parse(localStorage.getItem('admin_user') || '{}');
  const displayName = formatName(user.name);

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    history.push('/login');
  };

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50 text-slate-900">
      {/* Sidebar */}
      <aside className={`${sidebarOpen ? 'w-60' : 'w-[68px]'} transition-all duration-300 bg-white border-r border-slate-200 flex flex-col shrink-0 z-20`}>
        {/* Logo */}
        <div className="h-16 flex items-center px-4 border-b border-slate-200 gap-3">
          <img src="/logo.png" alt="Tapcoe" className="w-9 h-9 rounded-xl object-cover shrink-0 shadow-sm" />
          {sidebarOpen && (
            <div className="overflow-hidden">
              <h1 className="text-sm font-extrabold text-slate-900 tracking-wide">TAPCOE</h1>
              <p className="text-[10px] text-purple-700 font-semibold -mt-0.5">ADMIN PANEL</p>
            </div>
          )}
        </div>

        {/* Nav Links */}
        <nav className="flex-1 py-4 px-2 space-y-1 overflow-y-auto">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={`sidebar-link flex items-center gap-3 px-3 py-2.5 rounded-lg text-[13px] font-medium text-slate-600 ${location.pathname.startsWith(item.path) ? 'active' : ''}`}
              title={item.label}
            >
              <item.icon className="w-[18px] h-[18px] shrink-0" />
              {sidebarOpen && <span>{item.label}</span>}
            </NavLink>
          ))}
        </nav>

        {/* Bottom */}
        <div className="p-3 border-t border-slate-200">
          <button
            onClick={handleLogout}
            className="sidebar-link flex items-center gap-3 px-3 py-2.5 rounded-lg text-[13px] font-medium text-red-600 hover:text-red-700 hover:bg-red-50 w-full"
          >
            <FiLogOut className="w-[18px] h-[18px] shrink-0" />
            {sidebarOpen && <span>Logout</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden bg-slate-50">
        {/* Top Header */}
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-6 shrink-0 z-10 shadow-sm">
          <div className="flex items-center gap-4">
            <button onClick={() => setSidebarOpen(!sidebarOpen)} className="text-slate-500 hover:text-slate-900 p-1.5 rounded-lg hover:bg-slate-100 transition">
              {sidebarOpen ? <FiX className="w-5 h-5" /> : <FiMenu className="w-5 h-5" />}
            </button>
            <h2 className="text-sm font-bold text-slate-900 capitalize">
              {location.pathname.split('/').pop() || 'Dashboard'}
            </h2>
          </div>

          <div className="flex items-center gap-3">
            <div className="text-right">
              <div className="flex items-center justify-end gap-1.5">
                <p className="text-xs font-bold text-slate-900">{displayName}</p>
                {user.role && (
                  <span className="text-[9px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded font-semibold uppercase">
                    {user.role}
                  </span>
                )}
              </div>
              <p className="text-[10px] text-slate-500">{user.email || 'admin@tapcoe.com'}</p>
            </div>
            <div className="w-9 h-9 rounded-full bg-purple-700 flex items-center justify-center text-white text-xs font-bold">
              {displayName.charAt(0).toUpperCase()}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto p-6 bg-slate-50">
          <Switch>
            <Route exact path="/app/dashboard" component={Dashboard} />
            <Route exact path="/app/playables" component={PlayReels} />
            <Route exact path="/app/games" component={Games} />
            <Route exact path="/app/drops" component={Drops} />
            <Route exact path="/app/drops/create" component={CreateDrop} />
            <Route path="/app/drops/edit/:id" component={EditDrop} />
            <Route path="/app/contests" component={Contests} />
            <Route path="/app/users" component={Users} />
            <Route path="/app/categories" component={Categories} />
            <Route path="/app/cities" component={Cities} />
            <Route path="/app/partners" component={Partners} />
            <Route path="/app/verifications" component={PartnerVerifications} />
            <Route path="/app/coupons" component={Coupons} />
            <Route path="/app/reports" component={Reports} />
            <Route path="/app/settings" component={Settings} />
            <Route path="/app" render={() => <Dashboard />} />
          </Switch>
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
